import React from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import Sidebar from "../components/Layout/Sidebar";
import { useCapabilityMapState } from "../components/CapabilityMap/useCapabilityMapState";
import CapabilityMapFilterPanel from "../components/CapabilityMap/CapabilityMapFilterPanel";
import CapabilityMapSunburst from "../components/CapabilityMap/CapabilityMapSunburst";
import CapabilityMapHeatmap from "../components/CapabilityMap/CapabilityMapHeatmap";
import CapabilityMapPipelineView from "../components/CapabilityMap/CapabilityMapPipelineView";
import CapabilityMapViewSwitcher from "../components/CapabilityMap/CapabilityMapViewSwitcher";
import CapabilityMapSummaryBar from "../components/CapabilityMap/CapabilityMapSummaryBar";
import CapabilityMapDetailPanel from "../components/CapabilityMap/CapabilityMapDetailPanel";
import type { DomainMode } from "../components/CapabilityMap/types";

const DOMAIN_OPTIONS: { value: DomainMode; label: string }[] = [
  { value: "defense", label: "Defense" },
  { value: "industrial", label: "Industrial" },
  { value: "all", label: "All" },
];

const CapabilityMapView: React.FC = () => {
  const state = useCapabilityMapState();

  return (
    <Box sx={{ display: "flex", height: "100%", overflow: "hidden" }}>
      <Sidebar />

      <CapabilityMapFilterPanel
        dataset={state.dataset}
        filters={state.filters}
        domainMode={state.domainMode}
        searchQuery={state.searchQuery}
        onFiltersChange={state.setFilters}
        onDomainModeChange={state.setDomainMode}
        onSearchChange={state.setSearchQuery}
        onReset={state.resetFilters}
      />

      {/* Main content area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        {/* Top bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
            bgcolor: "background.default",
          }}
        >
          <CapabilityMapViewSwitcher
            viewMode={state.viewMode}
            onChange={state.setViewMode}
          />

          <Box sx={{ width: "1px", height: 24, bgcolor: "divider" }} />

          {/* Domain toggle */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Domain:
            </Typography>
            <ToggleButtonGroup
              value={state.domainMode}
              exclusive
              size="small"
              onChange={(_e, v) => {
                if (v) state.setDomainMode(v);
              }}
              sx={{
                "& .MuiToggleButton-root": {
                  px: 1,
                  py: 0.25,
                  fontSize: "0.7rem",
                },
              }}
            >
              {DOMAIN_OPTIONS.map(({ value, label }) => (
                <Tooltip
                  key={value}
                  title={`Show ${label.toLowerCase()} domain`}
                >
                  <ToggleButton value={value}>{label}</ToggleButton>
                </Tooltip>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Summary bar (hidden in pipeline view) */}
        {state.viewMode !== "pipeline" && (
          <CapabilityMapSummaryBar
            dataset={state.dataset}
            domainMode={state.domainMode}
            marketStatusFilter={state.marketStatusFilter}
            onFilterByStatus={state.setMarketStatusFilter}
          />
        )}

        {/* Canvas */}
        <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
          {state.viewMode === "sunburst" && (
            <CapabilityMapSunburst
              dataset={state.dataset}
              filters={state.filters}
              domainMode={state.domainMode}
              marketStatusFilter={state.marketStatusFilter}
              searchQuery={state.searchQuery}
              highlightedNodeIds={state.highlightedNodeIds}
              onNodeClick={state.handleNodeClick}
            />
          )}

          {state.viewMode === "heatmap" && (
            <CapabilityMapHeatmap
              dataset={state.dataset}
              filters={state.filters}
              domainMode={state.domainMode}
              marketStatusFilter={state.marketStatusFilter}
              searchQuery={state.searchQuery}
              highlightedNodeIds={state.highlightedNodeIds}
              heatmapMode={state.heatmapMode}
              onHeatmapModeChange={state.setHeatmapMode}
              onNodeClick={state.handleNodeClick}
            />
          )}

          {state.viewMode === "pipeline" && (
            <CapabilityMapPipelineView
              dataset={state.dataset}
              filters={state.filters}
              domainMode={state.domainMode}
              pipelineConfig={state.pipelineConfig}
              onConfigChange={state.setPipelineConfig}
              onNodeClick={state.handleNodeClick}
            />
          )}
        </Box>
      </Box>

      <CapabilityMapDetailPanel
        open={state.detailPanelOpen}
        selection={state.selection}
        dataset={state.dataset}
        onClose={state.closeDetailPanel}
        onNodeClick={state.handleNodeClick}
      />
    </Box>
  );
};

export default CapabilityMapView;
